import * as React from 'react';
import { st, classes } from './ColorPicker.base.st.css';
import { ColorPickerItem } from './ColorPickerItem';
import { ColorPickerBaseProps } from './types';
import { ErrorMessageWrapper } from '../internal/ErrorMessageWrapper';
import { DATA_HOOKS } from './dataHooks';
import uniqueId from '../../common/uniqueId';
import classnames from 'classnames';
import { Spinner, SpinnerTypes } from '../Spinner';
import { Asterisk } from '../internal/Asterisk/Asterisk';

export class ColorPickerBase extends React.Component<ColorPickerBaseProps> {
  private readonly _errorMessageId: string;
  static displayName = 'ColorPickerBase';
  static Item = ColorPickerItem;

  constructor(props) {
    super(props);

    this._errorMessageId = uniqueId('error-message');
  }

  render() {
    const {
      onChange,
      children,
      className,
      name,
      errorMessage,
      errorAppearance,
      label,
      suffix,
      isLoading = false,
      'loading-aria-label': loadingAriaLabel,
      required,
      showAsterisk,
    } = this.props;

    const stStates = { error: !!errorMessage };
    const renderRequiredAsterisk = required && showAsterisk;

    return (
      <ErrorMessageWrapper
        {...{
          visible: !!errorMessage,
          message: errorMessage,
          messageId: this._errorMessageId,
          className: st(classes.root, stStates, className),
          errorAppearance,
          'data-hook': this.props['data-hook'],
        }}
      >
        <fieldset
          className={classes.container}
          aria-label={this.props['aria-label']}
          aria-labelledby={this.props['aria-labelledby']}
          aria-required={this.props['aria-required']}
        >
          {!!label && (
            <legend
              data-hook={DATA_HOOKS.colorPickerFieldsetLabel}
              className={classes.label}
            >
              {label}
              {renderRequiredAsterisk && (
                <Asterisk dataHook={DATA_HOOKS.colorPickerLabelAsterisk} />
              )}
            </legend>
          )}
          <div
            className={classes.itemsContainer}
            aria-busy={isLoading}
            aria-live="polite"
          >
            {isLoading ? (
              <Spinner
                className={classes.loader}
                data-hook={DATA_HOOKS.colorPickerLoader}
                type={SpinnerTypes.slim}
                aria-label={loadingAriaLabel}
              />
            ) : (
              <div className={classes.wrapper}>
                {React.Children.map(children, (item, index: number) => {
                  if (!React.isValidElement(item)) {
                    return null;
                  }
                  return React.cloneElement(item, {
                    onChange: (event) => onChange(event, item.props.id),
                    key: index,
                    className: classnames(item.props.className, classes.item),
                    name,
                    required,
                  });
                })}
                {!!suffix && (
                  <span
                    className={classes.suffix}
                    data-hook={DATA_HOOKS.colorPickerSuffix}
                  >
                    {suffix}
                  </span>
                )}
              </div>
            )}
          </div>
        </fieldset>
      </ErrorMessageWrapper>
    );
  }
}
